import { useCallback, useEffect, useRef, useState } from 'react';
import {
  connectorsApi,
  isReauthable,
  type ConnectorAccount,
  type ConnectorItem,
  type LinkSession,
} from '@/api/connectors';
import { ErrorCodes, isApiError } from '@/api/errors';
import { getBankDataStripe, type FinancialConnectionsAccount } from '@/features/billing/stripeSdk';
import { usePlaidLink, type PlaidLinkError, type PlaidLinkOnSuccessMetadata } from './plaidLink';
import { clearPlaidLink, storePlaidLink } from './plaidLinkStore';

/** One bank account offered for mapping, whichever provider collected it. */
export interface BankAccountMeta {
  id: string;
  name: string;
  mask: string | null;
  institution: string;
  /** The provider's own type (Plaid `type`, Stripe `category`). */
  type: string;
  /** The provider's own subtype (Plaid `subtype`, Stripe `subcategory`). */
  subtype: string;
  currency: string | null;
}

interface MappingBase {
  /** The session's institution; empty when it holds more than one. */
  institution: string;
  accounts: BankAccountMeta[];
}

/** A new link waiting for the mapping dialog. The credential lives in memory only. */
export type MappingPayload =
  | (MappingBase & { provider: 'PLAID'; publicToken: string })
  | (MappingBase & { provider: 'STRIPE_FC'; sessionRef: string });

export type BankConnectStatus = 'idle' | 'loading' | 'unavailable' | 'error';

/** What a repair ended with — the caller picks the snack. */
export type ConnectOutcome = 'syncQueued' | 'reconnected' | 'needsMapping';

/** One connection account to open the single-account Map dialog on ("Map now", TMMAPP-0055). */
export interface MapTarget {
  item: ConnectorItem;
  account: ConnectorAccount;
}

export interface UseBankConnectOptions {
  itemId?: number | undefined;
  /** Plaid OAuth return only (`PlaidReturnPage`). */
  receivedRedirectUri?: string | undefined;
  /** Plaid OAuth return only: the stored link token to re-open Link with. */
  resumeToken?: string | undefined;
  /** `needsMapping` carries the first account the repair left needing a mapping. */
  onConnected?: ((outcome: ConnectOutcome, mapTarget?: MapTarget) => void) | undefined;
  /** The connection list is stale (a repair answered `CONNECTOR_NOT_REAUTHABLE`): refetch it. */
  onStale?: (() => void) | undefined;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function readPlaidAccounts(
  metadata: PlaidLinkOnSuccessMetadata | Record<string, unknown>,
  institution: string,
): BankAccountMeta[] {
  const w = asRecord(metadata);
  if (!Array.isArray(w.accounts)) return [];
  return w.accounts.flatMap((row) => {
    const account = asRecord(row);
    if (typeof account.id !== 'string' || account.id.length === 0) return [];
    const currency =
      typeof account.iso_currency_code === 'string'
        ? account.iso_currency_code
        : typeof account.currency === 'string'
          ? account.currency
          : null;
    return [
      {
        id: account.id,
        name: typeof account.name === 'string' ? account.name : '',
        mask: typeof account.mask === 'string' ? account.mask : null,
        institution,
        type: typeof account.type === 'string' ? account.type : '',
        subtype: typeof account.subtype === 'string' ? account.subtype : '',
        currency,
      },
    ];
  });
}

function plaidInstitution(metadata: PlaidLinkOnSuccessMetadata | Record<string, unknown>): string {
  const institution = asRecord(asRecord(metadata).institution);
  return typeof institution.name === 'string' ? institution.name : '';
}

/** The balance's currency, upper-cased, else USD — the server applies the same rule. */
function stripeCurrency(account: Record<string, unknown>): string {
  const current = asRecord(asRecord(account.balance).current);
  const code = Object.keys(current).find((key) => /^[a-z]{3}$/i.test(key));
  return code !== undefined ? code.toUpperCase() : 'USD';
}

export function readStripeAccounts(
  accounts: readonly FinancialConnectionsAccount[] | readonly unknown[],
): BankAccountMeta[] {
  return accounts.flatMap((row) => {
    const account = asRecord(row);
    if (typeof account.id !== 'string' || account.id.length === 0) return [];
    return [
      {
        id: account.id,
        name: typeof account.display_name === 'string' ? account.display_name : '',
        mask: typeof account.last4 === 'string' ? account.last4 : null,
        institution: typeof account.institution_name === 'string' ? account.institution_name : '',
        type: typeof account.category === 'string' ? account.category : '',
        subtype: typeof account.subcategory === 'string' ? account.subcategory : '',
        currency: stripeCurrency(account),
      },
    ];
  });
}

/**
 * The accounts a repair lost a mapping for (TMMAPP-0053), in the connection's order: an account
 * that was mapped before comes back unmapped (tmm-api keeps only mappings that existed when the
 * connection broke, TMMAPI-0096), or an account the connection didn't have (a new Stripe id
 * tmm-api couldn't match) arrives unmapped. An account the person left unmapped on purpose, and
 * still unmapped, is not a loss.
 */
export function accountsNeedingMapping<
  T extends Pick<ConnectorAccount, 'providerAccountId' | 'accountId'>,
>(
  before: readonly Pick<ConnectorAccount, 'providerAccountId' | 'accountId'>[],
  after: readonly T[],
): T[] {
  const mappedBefore = new Map(
    before.map((account) => [account.providerAccountId, account.accountId !== null]),
  );
  // `true`: mapped before, now lost; `undefined`: new to the connection; `false`: left unmapped.
  return after.filter(
    (account) =>
      account.accountId === null && mappedBefore.get(account.providerAccountId) !== false,
  );
}

function soleInstitution(accounts: BankAccountMeta[]): string {
  const names = new Set(accounts.map((account) => account.institution));
  return names.size === 1 ? [...names][0]! : '';
}

/**
 * The one bank-connect flow (TMMAPP-0052, MM-D-104). `start(itemId?)` asks tmm-api for a
 * `link-session` and opens whichever provider's window it names: Plaid Link (re-opened on the OAuth
 * return with Plaid's own link token, kept for that return only, and update mode → `sync`), or
 * Stripe Financial Connections (a new link → the
 * mapping dialog → `complete`; a repair → `complete` with `itemId`, reporting `needsMapping` only
 * when it lost a mapping — pass the connection's accounts to `start`). The Stripe `clientSecret` and
 * `sessionRef` stay in memory — never stored, never logged — and Stripe needs no return route.
 * TMMAPI-0096: a repair of a connection that no longer needs one (409 `CONNECTOR_NOT_REAUTHABLE`)
 * is `notReauthable` and asks the caller to refetch; a session tmm-api no longer has (404
 * `CONNECTOR_SESSION_NOT_FOUND`) is `sessionExpired` and back to idle — the next `start` asks for a
 * fresh `link-session`, and a spent `sessionRef` is never sent again.
 */
export function useBankConnect({
  itemId,
  receivedRedirectUri,
  resumeToken,
  onConnected,
  onStale,
}: UseBankConnectOptions = {}) {
  const [token, setToken] = useState<string | null>(resumeToken ?? null);
  const [activeItemId, setActiveItemId] = useState<number | undefined>(itemId);
  const [status, setStatus] = useState<BankConnectStatus>(resumeToken ? 'loading' : 'idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [institutionMismatch, setInstitutionMismatch] = useState(false);
  const [notReauthable, setNotReauthable] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [mapping, setMapping] = useState<MappingPayload | null>(null);
  const openedFor = useRef<string | null>(null);
  const activeItemRef = useRef(activeItemId);
  /** The repaired connection's accounts as the card showed them before the repair. */
  const accountsBeforeRef = useRef<readonly ConnectorAccount[]>([]);
  const onConnectedRef = useRef(onConnected);
  const onStaleRef = useRef(onStale);
  useEffect(() => {
    activeItemRef.current = activeItemId;
    onConnectedRef.current = onConnected;
    onStaleRef.current = onStale;
  }, [activeItemId, onConnected, onStale]);

  /** A repair the connection doesn't need: say so, and have the caller refetch the list. */
  const markNotReauthable = useCallback(() => {
    setNotReauthable(true);
    setStatus('error');
    onStaleRef.current?.();
  }, []);

  const handlePlaidSuccess = useCallback(
    (publicToken: string | null, metadata: PlaidLinkOnSuccessMetadata) => {
      clearPlaidLink();
      const currentItemId = activeItemRef.current;
      if (currentItemId !== undefined) {
        void (async () => {
          try {
            await connectorsApi.sync(currentItemId);
            onConnectedRef.current?.('syncQueued');
          } catch (error) {
            if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_UNAVAILABLE) {
              setStatus('unavailable');
              return;
            }
            setStatus('error');
          }
        })();
        return;
      }
      if (publicToken === null) {
        setStatus('error');
        return;
      }
      const institution = plaidInstitution(metadata);
      setMapping({
        provider: 'PLAID',
        publicToken,
        institution,
        accounts: readPlaidAccounts(metadata, institution),
      });
    },
    [],
  );

  const handlePlaidExit = useCallback((err: PlaidLinkError | null) => {
    clearPlaidLink();
    if (err === null || err === undefined) {
      setStatus('idle');
      return;
    }
    const message = err.display_message ?? '';
    setErrorMessage(message);
    setStatus('error');
  }, []);

  const linkConfig = {
    token,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
    ...(receivedRedirectUri !== undefined ? { receivedRedirectUri } : {}),
  };
  const { open, ready } = usePlaidLink(linkConfig);

  useEffect(() => {
    if (!ready || token === null) return;
    if (openedFor.current === token) return;
    openedFor.current = token;
    open();
    setStatus('idle');
  }, [ready, token, open]);

  const runStripe = useCallback(
    async (session: Extract<LinkSession, { provider: 'STRIPE_FC' }>, repairId?: number) => {
      const stripe = await getBankDataStripe(session.publishableKey);
      if (stripe === null) {
        setStatus('error');
        return;
      }
      const result = await stripe.collectFinancialConnectionsAccounts({
        clientSecret: session.clientSecret,
      });
      if (result.error !== undefined) {
        setErrorMessage(result.error.message ?? '');
        setStatus('error');
        return;
      }
      const accounts = readStripeAccounts(result.financialConnectionsSession.accounts);
      if (accounts.length === 0) {
        // Closed without choosing an account: the person cancelled.
        setStatus('idle');
        return;
      }
      if (repairId === undefined) {
        setMapping({
          provider: 'STRIPE_FC',
          sessionRef: session.sessionRef,
          institution: soleInstitution(accounts),
          accounts,
        });
        setStatus('idle');
        return;
      }
      // A repair: tmm-api matches the session's accounts to the connection's own; anything it
      // cannot match comes back unmapped and is mapped from the connection card. Only a lost or
      // new unmapped account asks for mapping, not one the person left unmapped.
      const item = await connectorsApi.complete({
        provider: 'STRIPE_FC',
        sessionRef: session.sessionRef,
        itemId: repairId,
        mappings: [],
      });
      setStatus('idle');
      const [first] = accountsNeedingMapping(accountsBeforeRef.current, item.accounts);
      if (first !== undefined) {
        onConnectedRef.current?.('needsMapping', { item, account: first });
        return;
      }
      onConnectedRef.current?.('reconnected');
    },
    [],
  );

  const start = useCallback(
    async (nextItemId?: number, accountsBefore?: readonly ConnectorAccount[]) => {
      const useItemId = nextItemId ?? itemId;
      setActiveItemId(useItemId);
      activeItemRef.current = useItemId;
      accountsBeforeRef.current = accountsBefore ?? [];
      setErrorMessage(null);
      setInstitutionMismatch(false);
      setNotReauthable(false);
      setSessionExpired(false);
      setMapping(null);
      setStatus('loading');
      openedFor.current = null;
      try {
        const session = await connectorsApi.linkSession(useItemId);
        if (session.provider === 'PLAID') {
          storePlaidLink({
            token: session.linkToken,
            ...(useItemId !== undefined ? { itemId: useItemId } : {}),
            startedAt: new Date().toISOString(),
            expiresAt: session.expiresAt,
          });
          setToken(session.linkToken);
          return;
        }
        // No stale Plaid token from an earlier session lingers behind a Stripe one.
        setToken(null);
        await runStripe(session, useItemId);
      } catch (error) {
        if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_UNAVAILABLE) {
          setStatus('unavailable');
          return;
        }
        if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_SESSION_NOT_FOUND) {
          setSessionExpired(true);
          setStatus('idle');
          return;
        }
        // From `complete {itemId}`, or from `link-session` should it ever answer it.
        if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_NOT_REAUTHABLE) {
          markNotReauthable();
          return;
        }
        if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_INSTITUTION_MISMATCH) {
          setInstitutionMismatch(true);
        }
        setStatus('error');
      }
    },
    [itemId, runStripe, markNotReauthable],
  );

  const dismissMapping = useCallback(() => {
    setMapping(null);
  }, []);

  /**
   * A new link's `complete` answered 404 `CONNECTOR_SESSION_NOT_FOUND` (the mapping dialog): drop
   * the session with its credential and go back to idle, so Connect starts afresh.
   */
  const expireSession = useCallback(() => {
    setMapping(null);
    setSessionExpired(true);
    setStatus('idle');
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage(null);
    setInstitutionMismatch(false);
    setNotReauthable(false);
    setSessionExpired(false);
    if (status === 'error') setStatus('idle');
  }, [status]);

  const retry = useCallback(() => {
    setStatus('idle');
    const repairId = activeItemRef.current;
    if (repairId === undefined) {
      void start(undefined, accountsBeforeRef.current);
      return;
    }
    // A retry never restarts a repair the connection no longer needs: read its status first. If
    // the list can't be read, tmm-api's 409 is the backstop.
    void (async () => {
      let current: ConnectorItem | undefined;
      try {
        current = (await connectorsApi.list()).find((item) => item.id === repairId);
      } catch {
        void start(repairId, accountsBeforeRef.current);
        return;
      }
      if (current === undefined || !isReauthable(current.status)) {
        markNotReauthable();
        return;
      }
      void start(repairId, accountsBeforeRef.current);
    })();
  }, [start, markNotReauthable]);

  return {
    start,
    status,
    mapping,
    dismissMapping,
    expireSession,
    errorMessage,
    institutionMismatch,
    notReauthable,
    sessionExpired,
    clearError,
    retry,
    unavailable: status === 'unavailable',
  };
}
